import { z } from "zod";

/**
 * The fields for managing an Organisation's workspaces on `/organization`
 * (TECH-3244/TECH-3200): create, rename, switch off, and bind to an external
 * reference. Each procedure's input in `organization.ts` adds the Organisation
 * it acts on, which the gate checks before the handler runs
 * (docs/adr/32-the-root-organisation-and-arranged-access.md).
 */

const MAX_WORKSPACE_NAME_LENGTH = 100;

/**
 * Trimmed before validating, so a name of nothing but spaces is refused here
 * rather than stored and rendered as a blank row.
 */
export const WorkspaceNameSchema = z
	.string()
	.trim()
	.min(1)
	.max(MAX_WORKSPACE_NAME_LENGTH);

/**
 * The identifier a workspace has in its Organization's external system, a
 * Strapi programId or an LTI context id, set by hand when a workspace was not
 * created from an import (TECH-3244).
 *
 * Nullable, and empty normalizes to null, because "no external reference" is a
 * real and common state. It is what a directly-created workspace has, and
 * clearing the field is how an admin says the binding was wrong. Storing `""`
 * instead would sit outside migration 009's partial unique index
 * (`externalRef: { $gt: "" }`) while still reading as a value to every consumer.
 *
 * Deliberately only a shape check. Whether the reference names anything
 * upstream is not knowable here, because LTI can only enumerate courses it has
 * already seen, so the server does not pretend to verify it and the UI says as
 * much.
 */
const MAX_EXTERNAL_REF_LENGTH = 512;

/**
 * The reference of an ExternalWorkspace in an Organisation's own system, as its
 * adapter lists it: always present, unlike a workspace's binding, which can be
 * cleared.
 */
export const ExternalWorkspaceRefSchema = z
	.string()
	.trim()
	.min(1)
	.max(MAX_EXTERNAL_REF_LENGTH);

export const WorkspaceExternalRefSchema = z
	.string()
	.trim()
	.max(MAX_EXTERNAL_REF_LENGTH)
	.nullable()
	.transform((ref) => (ref === "" ? null : ref));

/**
 * A workspace id on the wire. Deliberately only a shape check, and it
 * establishes nothing about whether the caller may act on that workspace. On
 * `/instance` the Root Organisation gate is the authority. On `/organization`
 * `assertWorkspaceInOrganization` verifies the id against the caller's own
 * Organization before any write.
 */
export const WorkspaceIdSchema = z.string().min(1);
